// Q Branch team sign-in. Two-step email → password flow that resolves
// against /api/curator/check-email + /api/curator/login. Used when a
// curator/admin wants to manage the marketplace without connecting a
// Salesforce org first. Sets the qbranch_curator cookie.
//
// 2026-05-03: collapsed the third "set-password" step. The server side
// stopped auto-creating curator passwords on first login (the old
// behaviour was a public account-takeover primitive), so the only
// legitimate flow is now email → password. First-time curators have to
// be initialized out-of-band; the server returns a clear 401 with the
// "Account not initialized" detail which we surface inline.

import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { curatorApi } from "@/lib/api/curator";
import { useUiStore } from "@/lib/store/uiStore";

export function TeamLogin(): JSX.Element {
  const navigate = useNavigate();
  const auth = useAuth();
  const showToast = useUiStore((s) => s.showToast);
  const [step, setStep] = useState<"email" | "password">("email");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    document.title = "Q Branch sign in";
  }, []);

  useEffect(() => {
    if (auth.curator?.logged_in) {
      navigate(auth.isAdmin ? "/admin" : "/curator", { replace: true });
    }
  }, [auth.curator?.logged_in, auth.isAdmin, navigate]);

  const checkEmail = async (): Promise<void> => {
    setError(null);
    setBusy(true);
    const res = await curatorApi.checkEmail(email.trim());
    setBusy(false);
    if (!res?.exists) {
      setError("That email isn't on the curators list. Ask an admin to add you.");
      return;
    }
    setStep("password");
  };

  const submitPassword = async (): Promise<void> => {
    setError(null);
    setBusy(true);
    try {
      await curatorApi.login(email.trim(), password);
      await auth.refetch();
      showToast("Signed in to Q Branch");
      navigate(auth.isAdmin ? "/admin" : "/curator");
    } catch (e) {
      setError((e as Error).message ?? "Sign-in failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="hero-gradient" style={{ minHeight: "calc(100vh - 52px)", padding: "60px 20px" }}>
      <div style={{ maxWidth: 440, margin: "0 auto" }}>
        <div className="card" style={{ padding: 28, textAlign: "left" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 18 }}>
            <img src="/assets/astro-happy.png" alt="" width={48} height={48} style={{ borderRadius: 12 }} />
            <div>
              <div className="label" style={{ marginBottom: 4 }}>Q Branch team</div>
              <h1 style={{ margin: 0, fontSize: 22, color: "var(--eb-15)" }}>Sign in</h1>
            </div>
          </div>
          <p style={{ fontSize: 12, color: "var(--ink-2)", marginTop: 0, marginBottom: 18 }}>
            Curators and admins can manage the pack marketplace without connecting a Salesforce org.
          </p>

          {step === "email" && (
            <>
              <Field label="Email">
                <input
                  type="email"
                  value={email}
                  autoFocus
                  onChange={(e) => setEmail(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && void checkEmail()}
                  placeholder="you@salesforce.com"
                  style={fieldInput}
                />
              </Field>
              <button
                className="btn primary"
                style={{ width: "100%", marginTop: 14 }}
                onClick={checkEmail}
                disabled={!email.trim() || busy}
              >
                {busy ? "Checking…" : "Continue"}
              </button>
            </>
          )}

          {step === "password" && (
            <>
              <Field label="Password">
                <input
                  type="password"
                  value={password}
                  autoFocus
                  onChange={(e) => setPassword(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && void submitPassword()}
                  style={fieldInput}
                />
              </Field>
              <button
                className="btn primary"
                style={{ width: "100%", marginTop: 14 }}
                onClick={submitPassword}
                disabled={!password || busy}
              >
                {busy ? "Signing in…" : "Sign in"}
              </button>
            </>
          )}

          {error && (
            <div style={{ marginTop: 12, padding: 10, background: "#fdf3f4", border: "1px solid #f3d3d6", borderRadius: 8, color: "var(--pink)", fontSize: 12 }}>
              {error}
            </div>
          )}

          <div style={{ marginTop: 22, paddingTop: 16, borderTop: "1px solid var(--bd-2)", textAlign: "center" }}>
            <button className="btn ghost sm" onClick={() => auth.connect()}>
              Or connect a Salesforce org →
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }): JSX.Element {
  return (
    <div style={{ marginTop: 12 }}>
      <div className="label" style={{ marginBottom: 4 }}>{label}</div>
      {children}
    </div>
  );
}

const fieldInput: React.CSSProperties = {
  width: "100%",
  background: "white",
  border: "1px solid var(--bd)",
  borderRadius: 6,
  padding: "10px 12px",
  fontSize: 13,
  outline: "none",
  boxSizing: "border-box",
};
